import { useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Calendar, FileText, Image, BookOpen, CheckSquare, LogOut } from 'lucide-react';
import LanguageToggle from '../components/LanguageToggle';
import VideoPlanner from './VideoPlanner';
import ScriptManager from './ScriptManager';
import AssetLibrary from './AssetLibrary';
import BlogManager from './BlogManager';
import SetupGuides from './SetupGuides';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  const navigation = [
    { name: t('Video Planner', 'مخطط الفيديو'), href: '/dashboard', icon: Calendar, exact: true },
    { name: t('Scripts', 'النصوص'), href: '/dashboard/scripts', icon: FileText },
    { name: t('Assets', 'الأصول'), href: '/dashboard/assets', icon: Image },
    { name: t('Blog', 'المدونة'), href: '/dashboard/blog', icon: BookOpen },
    { name: t('Setup Guides', 'أدلة الإعداد'), href: '/dashboard/setup', icon: CheckSquare },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.href;
    }
    return location.pathname.startsWith(item.href);
  };

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-2xl font-black tracking-tight text-safety-amber">
              {t('Anime Safety', 'أمان الأنيمي')}
            </h1>
            <div className="hidden md:flex items-center gap-2 text-sm text-[#A1A1AA]">
              <span>{user?.name}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Button
              data-testid="logout-button"
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-[#A1A1AA] hover:text-safety-amber transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-73px)] bg-carbon border-e border-[#27272A] p-4">
          <nav className="space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    active
                      ? 'bg-safety-amber text-obsidian font-semibold'
                      : 'text-[#A1A1AA] hover:bg-white/5 hover:text-mecha-blue'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <Routes>
            <Route index element={<VideoPlanner />} />
            <Route path="scripts" element={<ScriptManager />} />
            <Route path="assets" element={<AssetLibrary />} />
            <Route path="blog" element={<BlogManager />} />
            <Route path="setup" element={<SetupGuides />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
